import { useState, useEffect, useCallback } from 'react';
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { AppConfigDataClient, StartConfigurationSessionCommand, GetLatestConfigurationCommand } from "@aws-sdk/client-appconfigdata";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { fetchAuthSession } from '@aws-amplify/auth'


const APP_CONFIG = {
    application: 'tf60zwg',
    environment: 'development',
    configuration: 'gev2vnq',
    region: 'eu-central-1',
    identityPoolId: 'eu-central-1:b38b944e-4f1a-4297-9fae-3efca498ae5a',
    userPoolId: 'eu-central-1_lkUgNPoNr',
    pollIntervalSeconds: 300, // 5 minutes
    maxRetries: 3,  // Add retry configuration
    retryDelay: 1000 // 1 second between retries
  };

  export const useFeatureFlags = () => {
    const [flags, setFlags] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [configurationToken, setConfigurationToken] = useState(null);
  
    // Add retry logic
    const retry = useCallback(async (fn, retries = APP_CONFIG.maxRetries) => {
        try {
          return await fn();
        } catch (error) {
          if (retries > 0 && (error?.name === 'ResourceNotFoundException' || error?.$metadata?.httpStatusCode === 404)) {
            console.log(`Retrying... ${retries} attempts left`);
            await new Promise(resolve => setTimeout(resolve, APP_CONFIG.retryDelay));
            return retry(fn, retries - 1);
          }
          throw error;
        }
      }, []);
  
     // Memoize getFeatureFlags
  const getFeatureFlags = useCallback(async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens.idToken.toString();

      const cognitoIdentityClient = new CognitoIdentityClient({
        region: APP_CONFIG.region,
      });

      const credentials = fromCognitoIdentityPool({
        client: cognitoIdentityClient,
        identityPoolId: APP_CONFIG.identityPoolId,
        logins: {
          [`cognito-idp.${APP_CONFIG.region}.amazonaws.com/${APP_CONFIG.userPoolId}`]: idToken
        }
      });

      const appConfigDataClient = new AppConfigDataClient({
        region: APP_CONFIG.region,
        credentials: credentials
      });

      const sessionResponse = await retry(async () => {
        const startSessionCommand = new StartConfigurationSessionCommand({
          ApplicationIdentifier: APP_CONFIG.application,
          EnvironmentIdentifier: APP_CONFIG.environment,
          ConfigurationProfileIdentifier: APP_CONFIG.configuration,
          RequiredMinimumPollIntervalInSeconds: APP_CONFIG.pollIntervalSeconds
        });
        return appConfigDataClient.send(startSessionCommand);
      });

      const token = sessionResponse.InitialConfigurationToken;
      setConfigurationToken(token);

      const configResponse = await retry(async () => {
        const getConfigCommand = new GetLatestConfigurationCommand({
          ConfigurationToken: token
        });
        return appConfigDataClient.send(getConfigCommand);
      });

      if (configResponse.Configuration?.length > 0) {
        const configContent = new TextDecoder().decode(configResponse.Configuration);
        const featureFlags = JSON.parse(configContent);
        setFlags(featureFlags);
        setLoading(false);
        return featureFlags;
      } else {
        console.warn('Empty configuration received');
        setFlags({});
        setLoading(false);
        return {};
      }

    } catch (err) {
      console.error('Error fetching feature flags:', err);
      setError(err);
      setLoading(false);
      throw err;
    }
  }, [retry]);
  
    // Modified polling logic with retry
    useEffect(() => {
      let pollInterval;
  
      const pollForUpdates = async () => {
        if (!configurationToken) return;
  
        try {
          const { tokens } = await fetchAuthSession();
          const idToken = tokens.idToken.toString();
  
          const appConfigDataClient = new AppConfigDataClient({
            region: APP_CONFIG.region,
            credentials: fromCognitoIdentityPool({
              client: new CognitoIdentityClient({ region: APP_CONFIG.region }),
              identityPoolId: APP_CONFIG.identityPoolId,
              logins: {
                [`cognito-idp.${APP_CONFIG.region}.amazonaws.com/${APP_CONFIG.userPoolId}`]: idToken
              }
            })
          });
  
          const response = await retry(async () => {
            const getConfigCommand = new GetLatestConfigurationCommand({
              ConfigurationToken: configurationToken
            });
            return appConfigDataClient.send(getConfigCommand);
          });
  
          if (response.Configuration?.length > 0) {
            const configContent = new TextDecoder().decode(response.Configuration);
            const featureFlags = JSON.parse(configContent);
            setFlags(featureFlags);
          }
  
          setConfigurationToken(response.NextPollConfigurationToken);
  
        } catch (err) {
          console.error('Error polling for updates:', err);
          // If we get a 404, try to get a new session
          if (err?.$metadata?.httpStatusCode === 404) {
            try {
              await getFeatureFlags();
            } catch (refreshErr) {
              console.error('Error refreshing feature flags:', refreshErr);
            }
          }
        }
      };
  
      if (configurationToken) {
        pollInterval = setInterval(pollForUpdates, APP_CONFIG.pollIntervalSeconds * 1000);
      }
  
      return () => {
        if (pollInterval) {
          clearInterval(pollInterval);
        }
      };
    }, [configurationToken, getFeatureFlags, retry]);
  
    // Initial fetch with retry
    useEffect(() => {
      retry(getFeatureFlags);
    }, [getFeatureFlags, retry]);
  
    return {
      flags,
      loading,
      error,
      getFeatureFlags,
      refresh: () => retry(getFeatureFlags) // Add a refresh method
    };
  };
  
  export const isFlagEnabled = (flags, flagName) => {
    return flags?.[flagName]?.enabled === true;
  };
  